import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, FolderKanban, Archive, PackageSearch, LogOut, ShieldCheck, Calendar as CalendarIcon, Users as UsersIcon, Clock as ClockIcon, Truck as TruckIcon, Menu, X as XIcon, Building2, ClipboardList, Briefcase, Inbox, GraduationCap } from "lucide-react";
import { useAuth } from "../lib/auth";
import { Logo } from "./Logo";
import { roleLabel } from "../lib/format";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import api from "../lib/api";
import { launchTour, hasSeenTour } from "../lib/tour";
import { trackPopoutWindow, getSavedLayout, restoreSavedLayout, clearSavedLayout } from "../lib/sync";
import { toast } from "sonner";
import JarvisAssistant from "./JarvisAssistant";
import { Sparkles } from "lucide-react";

const HUD_KEY = "jc_hud_mode";
const applyHudClass = (on) => {
  try {
    document.body.classList.toggle("jc-hud", !!on);
  } catch {}
};

const ALL_LINKS = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testId: "nav-dashboard" },
  { to: "/sales", label: "Sales", icon: Briefcase, testId: "nav-sales" },
  { to: "/pending-approvals", label: "Pending Projects", icon: Inbox, testId: "nav-pending", badgeKey: "pending" },
  { to: "/projects", label: "Projects", icon: FolderKanban, testId: "nav-projects" },
  { to: "/deliveries", label: "Deliveries", icon: TruckIcon, testId: "nav-deliveries" },
  { to: "/calendar", label: "Calendar", icon: CalendarIcon, testId: "nav-calendar" },
  { to: "/archived", label: "Archived Projects", icon: Archive, testId: "nav-archived" },
  { to: "/inventory", label: "Warehouse Inventory", icon: PackageSearch, testId: "nav-inventory" },
  { to: "/off-site-deliveries", label: "Off-Site Deliveries", icon: TruckIcon, testId: "nav-off-site" },
  { to: "/vendors", label: "Vendors", icon: Building2, testId: "nav-vendors" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ClipboardList, testId: "nav-purchase-orders" },
  { to: "/users", label: "Users", icon: UsersIcon, testId: "nav-users", hideFor: ["sales_representative"] },
  { to: "/time", label: "Time Clock", icon: ClockIcon, testId: "nav-time", hideFor: ["sales_representative"] },
];

function visibleLinks(role) {
  return ALL_LINKS.filter((l) => !l.hideFor || !l.hideFor.includes(role));
}

function NavList({ onNavigate, role, badges }) {
  const links = visibleLinks(role);
  return (
    <nav className="flex-1 py-4 px-3 space-y-1 overflow-y-auto">
      {links.map(({ to, label, icon: Icon, testId, badgeKey }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          data-testid={testId}
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors ${
              isActive
                ? "bg-blue-800 text-white"
                : "text-slate-300 hover:bg-slate-800 hover:text-white"
            }`
          }
        >
          <Icon size={18} strokeWidth={2} />
          <span className="flex-1">{label}</span>
          {badgeKey && badges?.[badgeKey] > 0 && (
            <span data-testid={`nav-badge-${badgeKey}`} className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[10px] font-bold rounded-sm bg-amber-500 text-white">
              {badges[badgeKey]}
            </span>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

function UserFooter({ user, onLogout, onTakeTour, hudMode, onToggleHud }) {
  return (
    <div className="p-3 border-t border-slate-800">
      <div className="px-3 py-3 rounded-sm bg-slate-800/60">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-blue-200 font-semibold">
          <ShieldCheck size={14} />
          {roleLabel(user?.role)}
        </div>
        <div className="mt-1 text-sm font-semibold text-white" data-testid="current-user-name">
          {user?.name}
        </div>
        <div className="text-xs text-slate-400 truncate">{user?.email}</div>
      </div>
      <Button
        onClick={onToggleHud}
        data-testid="hud-toggle-btn"
        variant="ghost"
        className="w-full mt-2 justify-start text-slate-300 hover:text-white hover:bg-slate-800"
      >
        <Sparkles size={16} className="mr-2" /> {hudMode ? "HUD Mode: ON" : "HUD Mode: OFF"}
      </Button>
      <Button
        onClick={onTakeTour}
        data-testid="take-tour-btn"
        variant="ghost"
        className="w-full mt-1 justify-start text-slate-300 hover:text-white hover:bg-slate-800"
      >
        <GraduationCap size={16} className="mr-2" /> Take the tour
      </Button>
      <Button
        onClick={onLogout}
        data-testid="logout-btn"
        variant="ghost"
        className="w-full mt-1 justify-start text-slate-300 hover:text-white hover:bg-slate-800"
      >
        <LogOut size={16} className="mr-2" /> Sign out
      </Button>
    </div>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badges, setBadges] = useState({ pending: 0 });
  const [hudMode, setHudMode] = useState(() => {
    try { return localStorage.getItem(HUD_KEY) !== "0"; } catch { return true; }
  });
  // bump a counter on every path change so we can re-trigger the entrance animation
  const [routeTick, setRouteTick] = useState(0);
  useEffect(() => { setRouteTick((n) => n + 1); }, [location.pathname]);

  useEffect(() => {
    applyHudClass(hudMode);
    try { localStorage.setItem(HUD_KEY, hudMode ? "1" : "0"); } catch {}
  }, [hudMode]);

  // Set data-page attribute so HUD can swap its hero background per route
  useEffect(() => {
    const slug = (location.pathname.split("/")[1] || "dashboard").toLowerCase();
    try { document.body.setAttribute("data-page", slug); } catch {}
  }, [location.pathname]);
  useEffect(() => {
    let cancelled = false;
    const load = () => {
      api.get("/projects/pending")
        .then((r) => { if (!cancelled) setBadges({ pending: r.data.length }); })
        .catch(() => {});
    };
    load();
    const id = setInterval(load, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const role = user?.role;

  const startTour = () => {
    // Ensure the sidebar/nav items exist first — if we're on mobile, close the menu
    setMobileOpen(false);
    // Small delay to let any menu/sheet finish closing before highlighting elements
    setTimeout(() => launchTour({ role, userId: user?.id }), 300);
  };

  // Auto-launch the tour the first time this user signs in
  useEffect(() => {
    if (!user?.id) return;
    if (hasSeenTour(user.id)) return;
    const t = setTimeout(() => launchTour({ role: user.role, userId: user.id }), 900);
    return () => clearTimeout(t);
  }, [user?.id, user?.role]);

  const isPopout = typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("pop") === "1";

  useEffect(() => {
    if (!isPopout) return;
    // Track this window's position so the user can restore their layout later
    return trackPopoutWindow(window.location.pathname);
  }, [isPopout]);

  // Offer to restore saved popout layout once per session on the main window
  useEffect(() => {
    if (isPopout || !user?.id) return;
    const shown = sessionStorage.getItem("jc_restore_prompt_shown");
    if (shown) return;
    const layout = getSavedLayout();
    if (!Array.isArray(layout) || layout.length === 0) return;
    sessionStorage.setItem("jc_restore_prompt_shown", "1");
    const t = setTimeout(() => {
      toast(`Restore your ${layout.length}-window layout?`, {
        description: layout
          .slice(0, 3)
          .map((e) => e.path.replace(/^\//, "").replace(/-/g, " "))
          .join(" · "),
        duration: 12000,
        action: {
          label: "Restore",
          onClick: () => {
            const opened = restoreSavedLayout();
            if (opened === 0) {
              toast.error("Browser blocked the popup — allow pop-ups for this site and try again");
            } else {
              toast.success(`Reopened ${opened} window${opened === 1 ? "" : "s"}`);
            }
          },
        },
        cancel: {
          label: "Forget",
          onClick: () => { clearSavedLayout(); },
        },
      });
    }, 1200);
    return () => clearTimeout(t);
  }, [isPopout, user?.id]);

  return (
    <div className="min-h-screen md:flex bg-slate-50">
      {!isPopout && (
        <>
          {/* Desktop sidebar */}
          <aside className="hidden md:flex w-64 shrink-0 bg-slate-900 text-white flex-col no-print print-hide-sidebar" data-testid="app-sidebar">
            <div className="px-5 py-6 border-b border-slate-800">
              <Logo variant="dark" />
            </div>
            <NavList role={role} badges={badges} />
            <UserFooter user={user} onLogout={handleLogout} onTakeTour={startTour} hudMode={hudMode} onToggleHud={() => setHudMode((v) => !v)} />
          </aside>

          {/* Mobile top bar */}
          <div className="md:hidden sticky top-0 z-30 bg-slate-900 text-white flex items-center justify-between px-3 py-2 no-print" data-testid="mobile-topbar">
            <div className="flex items-center gap-2">
              <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Open menu"
                    data-testid="mobile-menu-btn"
                    className="text-white hover:bg-slate-800 h-10 w-10"
                  >
                    <Menu size={22} />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0 bg-slate-900 text-white border-r border-slate-800">
                  <div className="flex items-center justify-between px-5 py-4 border-b border-slate-800">
                    <Logo variant="dark" />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => setMobileOpen(false)}
                      className="text-white hover:bg-slate-800 h-8 w-8"
                      aria-label="Close menu"
                    >
                      <XIcon size={18} />
                    </Button>
                  </div>
                  <div className="flex flex-col h-[calc(100vh-64px)]">
                    <NavList role={role} badges={badges} onNavigate={() => setMobileOpen(false)} />
                    <UserFooter user={user} onLogout={() => { setMobileOpen(false); handleLogout(); }} onTakeTour={() => { setMobileOpen(false); startTour(); }} hudMode={hudMode} onToggleHud={() => setHudMode((v) => !v)} />
                  </div>
                </SheetContent>
              </Sheet>
              <Logo variant="dark" size="sm" />
            </div>
          </div>
        </>
      )}

      <main className="flex-1 min-w-0 relative">
        {/* Iron-Man style iris-reveal wrapper — re-keys on every route change */}
        <div
          key={`${location.pathname}-${routeTick}`}
          className="hud-route-enter"
          data-testid="route-transition-wrapper"
        >
          <Outlet />
        </div>
        {/* Overlay scan-line that sweeps on page change (HUD only) */}
        <div key={`sweep-${routeTick}`} className="hud-route-sweep" aria-hidden="true" />
      </main>

      {!isPopout && <JarvisAssistant />}
    </div>
  );
}
